/**
 * A single proposal list item — shared by the account Proposals tab/Overview
 * and the prospect Proposals tab.
 *
 * Quick Proposals (and, once Fission exposes a stable URL, Informed Analysis
 * runs) render a link + access-code block with copy/open actions. Informed
 * Analysis runs that don't have a URL yet fall back to an inline "view results"
 * action via `onView`; when `onView` is omitted (e.g. the read-only Overview)
 * they render as a static row. When `onDelete` is omitted the row is read-only.
 */

import React from 'react';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  FileText,
  Trash2,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';

export interface ProposalRowData {
  id: string;
  type: 'QUICK_PROPOSAL' | 'INFORMED_ANALYSIS';
  url: string | null;
  resourceId: string | null;
  password: string | null;
  // Absolute Infinity-hosted proposal URL, fully resolved server-side (tenant
  // domain in prod, request origin in dev). Null for external-generator
  // proposals, which carry an external `url` instead.
  infinityHostedUrl?: string | null;
  label: string | null;
  createdAt: Date | string;
  uploadedByUser: {name: string | null} | null;
}

interface ProposalRowProps {
  proposal: ProposalRowData;
  companyName?: string | null;
  // Informed Analysis interim: open the inline report viewer. Omit to render IA
  // rows (without a URL) as static — e.g. on the read-only Overview.
  onView?: (proposalId: string) => void;
  // Omit to make the row read-only (no delete affordance).
  onDelete?: (proposalId: string) => void;
  // When true, the link/access-code block is hidden behind a chevron toggle
  // (collapsed by default). Used by the read-only Overview.
  collapsible?: boolean;
}

export function ProposalRow({
  proposal: p,
  companyName,
  onView,
  onDelete,
  collapsible = false,
}: ProposalRowProps): React.ReactElement {
  const isIa = p.type === 'INFORMED_ANALYSIS';
  // The Infinity-hosted link (resolved server-side, absolute) takes precedence
  // over any external generator `url`.
  const linkUrl = p.infinityHostedUrl ?? p.url;
  const hasUrl = !!linkUrl;
  const [copied, setCopied] = React.useState<'url' | 'password' | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const showDetails = !collapsible || expanded;

  const copy = async (field: 'url' | 'password', text: string): Promise<void> => {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const pill = (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isIa
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-blue-200 bg-blue-50 text-blue-700'
      }`}
    >
      {isIa ? 'Informed Analysis' : 'Quick Proposal'}
    </span>
  );

  const metaLine = (
    <div className='mt-0.5 text-xs text-muted-foreground'>
      {p.uploadedByUser?.name ?? 'Unknown'} · {new Date(p.createdAt).toLocaleString()}
    </div>
  );

  const deleteButton = onDelete ? (
    <Button
      size='sm'
      variant='ghost'
      className='shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100'
      onClick={() => onDelete(p.id)}
    >
      <Trash2 className='h-3.5 w-3.5' />
    </Button>
  ) : null;

  const chevronButton = collapsible ? (
    <Button
      type='button'
      size='sm'
      variant='ghost'
      className='shrink-0 text-muted-foreground'
      onClick={() => setExpanded((e) => !e)}
      aria-label={expanded ? 'Hide details' : 'Show details'}
    >
      <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </Button>
  ) : null;

  const title = p.label ?? companyName ?? 'Proposal';

  // Informed Analysis that has no stable URL yet → inline "view results" action.
  if (isIa && !hasUrl) {
    return (
      <div className='group flex items-start justify-between gap-3 rounded-lg border border-neutral-200/60 bg-muted/20 p-3 transition-colors hover:bg-muted/40'>
        <div className='flex min-w-0 flex-1 items-start gap-3'>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
            <BarChart3 className='h-4 w-4' />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex items-center gap-2'>
              {onView ? (
                <button
                  type='button'
                  onClick={() => onView(p.id)}
                  className='inline-flex min-w-0 items-center gap-1 text-sm font-medium text-primary hover:underline'
                >
                  <span className='truncate'>
                    View Informed Analysis results{p.label ? ` for ${p.label}` : ''}
                  </span>
                  <ArrowRight className='h-3 w-3 shrink-0' />
                </button>
              ) : (
                <p className='truncate text-sm font-medium'>{title}</p>
              )}
              {pill}
            </div>
            {metaLine}
          </div>
        </div>
        {deleteButton}
      </div>
    );
  }

  // Quick Proposal (or Informed Analysis once it has a URL): link + access code.
  return (
    <div className='group rounded-lg border border-neutral-200/60 bg-muted/20 p-3 transition-colors hover:bg-muted/40'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 flex-1 items-start gap-3'>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
            {isIa ? <BarChart3 className='h-4 w-4' /> : <FileText className='h-4 w-4' />}
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex items-center gap-2'>
              <p className='truncate text-sm font-medium'>{title}</p>
              {pill}
            </div>
            {metaLine}
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          {chevronButton}
          {deleteButton}
        </div>
      </div>

      {showDetails ? (
        <div className='mt-3 max-w-4xl space-y-2.5 pl-12'>
          <div>
            <p className='mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Link
            </p>
            <div className='flex gap-2'>
              <Input value={linkUrl ?? ''} readOnly className='min-w-0 flex-1 font-mono text-xs' />
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='shrink-0 rounded-lg'
                onClick={() => linkUrl && copy('url', linkUrl)}
              >
                {copied === 'url' ? (
                  <Check className='h-4 w-4 text-emerald-500' />
                ) : (
                  <Copy className='h-4 w-4' />
                )}
              </Button>
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='shrink-0 rounded-lg'
                onClick={() => linkUrl && window.open(linkUrl, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className='h-4 w-4' />
              </Button>
            </div>
          </div>

          {p.password ? (
            <div>
              <p className='mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
                Access code
              </p>
              <div className='flex gap-2'>
                <Input value={p.password} readOnly className='min-w-0 flex-1 font-mono text-xs' />
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  className='shrink-0 rounded-lg'
                  onClick={() => p.password && copy('password', p.password)}
                >
                  {copied === 'password' ? (
                    <Check className='h-4 w-4 text-emerald-500' />
                  ) : (
                    <Copy className='h-4 w-4' />
                  )}
                </Button>
                {/* Invisible spacer so the access-code input lines up with the
                  link input (which has an extra open button). */}
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  className='invisible shrink-0 rounded-lg'
                  aria-hidden
                  tabIndex={-1}
                >
                  <ExternalLink className='h-4 w-4' />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
